import {
  ControlOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Alert, Button, Card, Drawer, Input, Popconfirm, Select, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type FileEntryCondition,
  type OneSpanConfig,
  type SignatoryFieldMappings,
  type SignatoryRole,
  type SignatureDocument,
  type WorkflowStep,
  type WorkflowTemplate,
  workflowsApi,
} from '@/api/workflows';
import { ICON_BTN_PROPS } from '@/components/iconButton';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { ConditionsEditorModal } from './conditions/ConditionsEditorModal';
import { hasEligibleSourceFields } from './conditions/sourceFields';
import {
  collectFieldChoices,
  collectPdfEntries,
  describeField,
  EMAIL_FIELD_TYPES,
  NAME_FIELD_TYPES,
  PHONE_FIELD_TYPES,
} from './oneSpanHelpers';

const DEFAULT_LABEL_EN = 'Signatory';
const DEFAULT_LABEL_FR = 'Signataire';

const { Text } = Typography;
const I18N = 'workflow.steps.oneSpan';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const ADVISOR_SLUG = 'advisor';

/**
 * Per-signer authoring drawer for the OneSpanESign step. Each signer carries
 * their own list of placements (PDF + anchor) — at save time we transform
 * back into BMO's documents[].fields[] wire shape by grouping placements by
 * PDF entry id.
 *
 * Layout:
 *   - Advisor card (singleton, pinned at top, no contact mapping — info comes
 *     from the BFF/JWT session at runtime).
 *   - Applicant cards below (1..N), each with 4 contact-field selects + their
 *     own placements list.
 *
 * Slug derivation: the advisor's wire slug is hard-coded "advisor"; applicant
 * slugs are derived from array position as "applicant", "applicant_2", … so
 * the originator never types or sees them.
 */
type Props = {
  templateId: string;
  workflow: WorkflowTemplate;
  step: WorkflowStep | null;
  editable: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
};

type ApplicantDraft = {
  _uid: string;
  /** Wire slug captured at load time (e.g. "applicant_2"). On save we
   *  PREFER this over a freshly-derived position slug so downstream
   *  consumers (OneSpan webhook callbacks, audit logs) don't rebind when
   *  applicants are deleted/reordered. Null for fresh applicants — they
   *  pick up the lowest free position-derived slug at save time. */
  persistedSlug: string | null;
  nameFieldId: string | null;
  lastNameFieldId: string | null;
  emailFieldId: string | null;
  phoneFieldId: string | null;
  /** Originator-typed display name shown wherever this signer is rendered
   *  (e.g. the runtime preview tag). Defaults to "Signatory"/"Signataire"
   *  but is fully editable (e.g. "Co-borrower", "Guarantor"). */
  labelEn: string;
  labelFr: string;
  /** Gating conditions — does this signer get included in the signing
   *  package. Empty list = always included. */
  conditionCombinator: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
  placements: PlacementDraft[];
};

type AdvisorDraft = {
  placements: PlacementDraft[];
};

type PlacementDraft = {
  _uid: string;
  /** Wire `name`/`binding` captured at load time. Preserving them across
   *  saves means `sig_field_1` stays bound to the same logical placement
   *  even after rows are added/deleted/reordered — keeps OneSpan webhook
   *  callbacks stable. Null for fresh rows — they get the lowest free
   *  per-doc number at save time. */
  persistedName: string | null;
  persistedBinding: string | null;
  pdfEntryId: string | null;
  textAnchor: string;
};

export function OneSpanConfigDrawer({
  templateId,
  workflow,
  step,
  editable,
  onClose,
  onChanged,
}: Props) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const [advisor, setAdvisor] = useState<AdvisorDraft>({ placements: [] });
  const [applicants, setApplicants] = useState<ApplicantDraft[]>([]);
  const [saving, setSaving] = useState(false);
  // Index of the applicant whose Conditions modal is open. Local-only — the
  // condition draft lives on the applicant itself and is persisted with the
  // rest of the drawer on Save, same as placements/labels.
  const [conditionsTargetIdx, setConditionsTargetIdx] = useState<number | null>(null);

  // Reset draft state ONLY when the drawer opens against a different step. The
  // 20s editor-lock poll generates new step prop refs even with unchanged
  // contents; gating on step.id keeps the originator's edits intact between
  // background refreshes.
  const initializedForStepIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!step) {
      initializedForStepIdRef.current = null;
      return;
    }
    if (initializedForStepIdRef.current === step.id) return;
    initializedForStepIdRef.current = step.id;
    const { advisor: a, applicants: ap } = draftsFromConfig(step.oneSpanConfig);
    setAdvisor(a);
    setApplicants(ap);
  }, [step]);

  const pdfChoices = useMemo(() => collectPdfEntries(workflow, isFr), [workflow, isFr]);
  const nameFieldChoices = useMemo(
    () => collectFieldChoices(workflow, isFr, NAME_FIELD_TYPES),
    [workflow, isFr],
  );
  const emailFieldChoices = useMemo(
    () => collectFieldChoices(workflow, isFr, EMAIL_FIELD_TYPES),
    [workflow, isFr],
  );
  const phoneFieldChoices = useMemo(
    () => collectFieldChoices(workflow, isFr, PHONE_FIELD_TYPES),
    [workflow, isFr],
  );

  if (!step) return null;

  const stepTitle = (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr;
  const validationError = validateDraft(advisor, applicants, pdfChoices, t);

  const handleSave = async () => {
    if (validationError) {
      message.error(validationError);
      return;
    }
    setSaving(true);
    try {
      const payload = buildWirePayload(advisor, applicants);
      await workflowsApi.setOneSpanConfig(templateId, step.id, payload);
      message.success(t(`${I18N}.saved`));
      await onChanged();
      onClose();
    } catch (err) {
      handleApiError(err, t, t(`${I18N}.saveFailed`));
    } finally {
      setSaving(false);
    }
  };

  // ---- Applicant handlers --------------------------------------------------
  // All mutators use the functional-updater form so rapid sequential updates
  // (double-click on Add, or onChange firing alongside a state-derived effect)
  // can't drop work by reading a stale closure snapshot.

  const addApplicant = () => {
    setApplicants((prev) => [
      ...prev,
      {
        _uid: newUid(),
        persistedSlug: null,
        nameFieldId: null,
        lastNameFieldId: null,
        emailFieldId: null,
        phoneFieldId: null,
        labelEn: DEFAULT_LABEL_EN,
        labelFr: DEFAULT_LABEL_FR,
        conditionCombinator: null,
        conditions: [],
        placements: [],
      },
    ]);
  };

  const updateApplicant = (i: number, patch: Partial<ApplicantDraft>) => {
    setApplicants((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  };

  const deleteApplicant = (i: number) => {
    setApplicants((prev) => prev.filter((_, idx) => idx !== i));
  };

  // ---- Placement handlers (shared between advisor + applicants) -----------

  const addPlacement = (target: 'advisor' | number) => {
    const blank: PlacementDraft = {
      _uid: newUid(),
      persistedName: null,
      persistedBinding: null,
      pdfEntryId: null,
      textAnchor: '',
    };
    if (target === 'advisor') {
      setAdvisor((prev) => ({ ...prev, placements: [...prev.placements, blank] }));
    } else {
      setApplicants((prev) =>
        prev.map((a, idx) => (idx === target ? { ...a, placements: [...a.placements, blank] } : a)),
      );
    }
  };

  const updatePlacement = (
    target: 'advisor' | number,
    placementIdx: number,
    patch: Partial<PlacementDraft>,
  ) => {
    if (target === 'advisor') {
      setAdvisor((prev) => ({
        ...prev,
        placements: prev.placements.map((p, i) => (i === placementIdx ? { ...p, ...patch } : p)),
      }));
    } else {
      setApplicants((prev) =>
        prev.map((a, idx) =>
          idx === target
            ? {
                ...a,
                placements: a.placements.map((p, i) =>
                  i === placementIdx ? { ...p, ...patch } : p,
                ),
              }
            : a,
        ),
      );
    }
  };

  const deletePlacement = (target: 'advisor' | number, placementIdx: number) => {
    if (target === 'advisor') {
      setAdvisor((prev) => ({
        ...prev,
        placements: prev.placements.filter((_, i) => i !== placementIdx),
      }));
    } else {
      setApplicants((prev) =>
        prev.map((a, idx) =>
          idx === target
            ? { ...a, placements: a.placements.filter((_, i) => i !== placementIdx) }
            : a,
        ),
      );
    }
  };

  return (
    <Drawer
      open={!!step}
      onClose={onClose}
      title={t(`${I18N}.drawerTitle`, { stepTitle })}
      width={640}
      destroyOnHidden
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div>
            {validationError && editable && (
              <Text type="warning" style={{ fontSize: 12 }}>
                {validationError}
              </Text>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={onClose} disabled={saving}>
              {t(`${I18N}.cancel`)}
            </Button>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={!editable || !!validationError}
              loading={saving}
            >
              {t(`${I18N}.save`)}
            </Button>
          </div>
        </div>
      }
    >
      {/* ---- Advisor (singleton, pinned at top) ---- */}

      <SignerSectionHeading
        title={t(`${I18N}.advisorTitle`)}
        help={t(`${I18N}.advisorAutoFilled`)}
      />

      <SignerCard
        kind="advisor"
        placements={advisor.placements}
        pdfChoices={pdfChoices}
        disabled={!editable}
        onAddPlacement={() => addPlacement('advisor')}
        onUpdatePlacement={(idx, patch) => updatePlacement('advisor', idx, patch)}
        onDeletePlacement={(idx) => deletePlacement('advisor', idx)}
      />

      <div style={{ height: 20 }} />

      {/* ---- Applicants (1..N) ---- */}

      <SignerSectionHeading
        title={t(`${I18N}.signatoriesTitle`)}
        help={t(`${I18N}.signatoriesHelp`)}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {applicants.map((applicant, i) => (
          <SignerCard
            key={applicant._uid}
            kind="applicant"
            applicantNumber={i + 1}
            applicant={applicant}
            placements={applicant.placements}
            nameChoices={nameFieldChoices}
            emailChoices={emailFieldChoices}
            phoneChoices={phoneFieldChoices}
            pdfChoices={pdfChoices}
            disabled={!editable}
            hasConditionablesFields={hasEligibleSourceFields(workflow.steps, {
              kind: 'step',
              ownStepId: step.id,
            })}
            onChangeApplicant={(patch) => updateApplicant(i, patch)}
            onDeleteApplicant={() => deleteApplicant(i)}
            onEditConditions={() => setConditionsTargetIdx(i)}
            onAddPlacement={() => addPlacement(i)}
            onUpdatePlacement={(idx, patch) => updatePlacement(i, idx, patch)}
            onDeletePlacement={(idx) => deletePlacement(i, idx)}
          />
        ))}
        <Button icon={<PlusOutlined />} onClick={addApplicant} disabled={!editable} type="dashed">
          {t(`${I18N}.addSignatory`)}
        </Button>
      </div>

      <ConditionsEditorModal
        open={conditionsTargetIdx !== null}
        title={t(`${I18N}.conditionModalTitle`)}
        intro={t(`${I18N}.conditionIntro`)}
        initialConditions={
          conditionsTargetIdx !== null ? applicants[conditionsTargetIdx]!.conditions : []
        }
        initialCombinator={
          conditionsTargetIdx !== null ? applicants[conditionsTargetIdx]!.conditionCombinator : null
        }
        allSteps={workflow.steps}
        scope={{ kind: 'step', ownStepId: step.id }}
        onCancel={() => setConditionsTargetIdx(null)}
        onSave={(conditions, combinator) => {
          if (conditionsTargetIdx === null) return;
          updateApplicant(conditionsTargetIdx, { conditions, conditionCombinator: combinator });
          setConditionsTargetIdx(null);
        }}
      />

      {pdfChoices.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message={t(`${I18N}.placementsNoPdfs`)}
        />
      )}
    </Drawer>
  );
}

// ---- Sub-components --------------------------------------------------------

function SignerSectionHeading({ title, help }: { title: string; help: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <Text strong style={{ fontSize: 14, display: 'block' }}>
        {title}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {help}
      </Text>
    </div>
  );
}

type SignerCardProps =
  | {
      kind: 'advisor';
      placements: PlacementDraft[];
      pdfChoices: ReturnType<typeof collectPdfEntries>;
      disabled: boolean;
      onAddPlacement: () => void;
      onUpdatePlacement: (idx: number, patch: Partial<PlacementDraft>) => void;
      onDeletePlacement: (idx: number) => void;
    }
  | {
      kind: 'applicant';
      applicantNumber: number;
      applicant: ApplicantDraft;
      placements: PlacementDraft[];
      nameChoices: ReturnType<typeof collectFieldChoices>;
      emailChoices: ReturnType<typeof collectFieldChoices>;
      phoneChoices: ReturnType<typeof collectFieldChoices>;
      pdfChoices: ReturnType<typeof collectPdfEntries>;
      disabled: boolean;
      /** Whether any prior-step field exists for the conditions picker to
       *  offer — hides the Conditions button entirely when there's nothing
       *  the originator could ever pick (e.g. the OneSpanESign step is the
       *  workflow's very first step). */
      hasConditionablesFields: boolean;
      onChangeApplicant: (patch: Partial<ApplicantDraft>) => void;
      onDeleteApplicant: () => void;
      onEditConditions: () => void;
      onAddPlacement: () => void;
      onUpdatePlacement: (idx: number, patch: Partial<PlacementDraft>) => void;
      onDeletePlacement: (idx: number) => void;
    };

function SignerCard(props: SignerCardProps) {
  const { t } = useTranslation();
  const isApplicant = props.kind === 'applicant';
  return (
    <Card
      size="small"
      style={{ background: '#fafafa', borderColor: '#e5e7eb' }}
      styles={{
        body: { padding: '20px 18px 18px' },
        header: { padding: '14px 18px', borderBottom: '1px solid #eef2f6', minHeight: 'auto' },
      }}
      title={
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Advisor card has no order badge — wire signing order is variable
              (and the advisor is appended LAST in buildWirePayload), so a
              hard-coded "1" badge would mislead. Applicants get their
              position-derived badge. */}
          {isApplicant && (
            <OrderBadge value={props.applicantNumber} ariaLabel={t(`${I18N}.index`)} />
          )}
          <Text strong style={{ fontSize: 14 }}>
            {isApplicant ? t(`${I18N}.typeSignatory`) : t(`${I18N}.typeAdvisor`)}
          </Text>
        </div>
      }
      extra={
        isApplicant ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {props.applicant.conditions.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(`${I18N}.conditionallyN`, { count: props.applicant.conditions.length })}
              </Text>
            )}
            {props.hasConditionablesFields && (
              <Tooltip
                title={t(
                  props.applicant.conditions.length > 0
                    ? `${I18N}.conditionEdit`
                    : `${I18N}.conditionAdd`,
                )}
              >
                <Button
                  {...ICON_BTN_PROPS}
                  icon={
                    <ControlOutlined
                      style={{
                        color: props.applicant.conditions.length > 0 ? '#0179C1' : undefined,
                      }}
                    />
                  }
                  disabled={props.disabled}
                  onClick={props.onEditConditions}
                />
              </Tooltip>
            )}
            <Popconfirm
              title={t(`${I18N}.deleteSignatoryConfirm`)}
              onConfirm={props.onDeleteApplicant}
              okButtonProps={{ danger: true }}
              disabled={props.disabled}
            >
              <Tooltip title={t(`${I18N}.deleteSignatory`)}>
                <Button
                  {...ICON_BTN_PROPS}
                  danger
                  icon={<DeleteOutlined />}
                  disabled={props.disabled}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        ) : null
      }
    >
      {isApplicant && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
          <DisplayNameRow
            labelEn={props.applicant.labelEn}
            labelFr={props.applicant.labelFr}
            disabled={props.disabled}
            onChange={(patch) => props.onChangeApplicant(patch)}
          />
          <FieldMappingRow
            label={t(`${I18N}.mapName`)}
            required
            value={props.applicant.nameFieldId}
            choices={props.nameChoices}
            disabled={props.disabled}
            onChange={(id) => props.onChangeApplicant({ nameFieldId: id })}
          />
          <FieldMappingRow
            label={t(`${I18N}.mapLastName`)}
            required={false}
            value={props.applicant.lastNameFieldId}
            choices={props.nameChoices}
            disabled={props.disabled}
            onChange={(id) => props.onChangeApplicant({ lastNameFieldId: id })}
          />
          <FieldMappingRow
            label={t(`${I18N}.mapEmail`)}
            required
            value={props.applicant.emailFieldId}
            choices={props.emailChoices}
            disabled={props.disabled}
            onChange={(id) => props.onChangeApplicant({ emailFieldId: id })}
          />
          <FieldMappingRow
            label={t(`${I18N}.mapPhone`)}
            required
            value={props.applicant.phoneFieldId}
            choices={props.phoneChoices}
            disabled={props.disabled}
            onChange={(id) => props.onChangeApplicant({ phoneFieldId: id })}
          />
        </div>
      )}

      {/* Per-signer placement list */}
      <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
        {t(`${I18N}.signLocationsLabel`)}
      </Text>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
        {t(`${I18N}.signLocationsHelp`)}
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {props.placements.map((p, idx) => (
          <PlacementRow
            key={p._uid}
            placement={p}
            pdfChoices={props.pdfChoices}
            disabled={props.disabled}
            onChange={(patch) => props.onUpdatePlacement(idx, patch)}
            onDelete={() => props.onDeletePlacement(idx)}
          />
        ))}
        <Button
          icon={<PlusOutlined />}
          type="dashed"
          onClick={props.onAddPlacement}
          disabled={props.disabled || props.pdfChoices.length === 0}
          size="small"
          style={{ alignSelf: 'flex-start' }}
        >
          {t(`${I18N}.addPlacement`)}
        </Button>
      </div>
    </Card>
  );
}

function OrderBadge({ value, ariaLabel }: { value: number; ariaLabel: string }) {
  return (
    <span
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 32,
        height: 32,
        padding: '0 10px',
        borderRadius: 6,
        background: '#fff',
        border: '1px solid #d9d9d9',
        fontSize: 14,
        fontWeight: 600,
        color: '#475569',
        lineHeight: 1,
        boxSizing: 'border-box',
      }}
    >
      {value}
    </span>
  );
}

function DisplayNameRow({
  labelEn,
  labelFr,
  disabled,
  onChange,
}: {
  labelEn: string;
  labelFr: string;
  disabled: boolean;
  onChange: (patch: { labelEn?: string; labelFr?: string }) => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#475569' }}>
          {t(`${I18N}.displayNameEn`)}
          <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>
        </div>
        <Input
          value={labelEn}
          onChange={(e) => onChange({ labelEn: e.target.value })}
          disabled={disabled}
        />
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#475569' }}>
          {t(`${I18N}.displayNameFr`)}
        </div>
        <Input
          value={labelFr}
          onChange={(e) => onChange({ labelFr: e.target.value })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function FieldMappingRow({
  label,
  required,
  value,
  choices,
  disabled,
  onChange,
}: {
  label: string;
  required: boolean;
  value: string | null;
  choices: ReturnType<typeof collectFieldChoices>;
  disabled: boolean;
  onChange: (id: string | null) => void;
}) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#475569' }}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
      </div>
      <FieldChoiceSelect value={value} choices={choices} disabled={disabled} onChange={onChange} />
    </div>
  );
}

function FieldChoiceSelect({
  value,
  choices,
  onChange,
  disabled,
}: {
  value: string | null;
  choices: ReturnType<typeof collectFieldChoices>;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Select
      value={value ?? undefined}
      onChange={(v) => onChange(v ?? null)}
      placeholder={t(`${I18N}.selectField`)}
      style={{ width: '100%' }}
      allowClear
      showSearch
      optionFilterProp="label"
      notFoundContent={t(`${I18N}.selectFieldEmpty`)}
      options={choices.map((c) => ({ value: c.id, label: describeField(c) }))}
      disabled={disabled}
    />
  );
}

function PlacementRow({
  placement,
  pdfChoices,
  disabled,
  onChange,
  onDelete,
}: {
  placement: PlacementDraft;
  pdfChoices: ReturnType<typeof collectPdfEntries>;
  disabled: boolean;
  onChange: (patch: Partial<PlacementDraft>) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const pdfKnown = !placement.pdfEntryId || pdfChoices.some((p) => p.id === placement.pdfEntryId);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 36px',
        gap: 8,
        alignItems: 'end',
      }}
    >
      <div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
          {t(`${I18N}.pdfLabel`)}
        </div>
        <Select
          value={placement.pdfEntryId ?? undefined}
          onChange={(v) => onChange({ pdfEntryId: v ?? null })}
          placeholder={t(`${I18N}.pdfSelect`)}
          style={{ width: '100%' }}
          status={!pdfKnown ? 'error' : undefined}
          allowClear
          options={pdfChoices.map((p) => ({
            value: p.id,
            label: `${p.stepLabel} › ${p.label}`,
          }))}
          disabled={disabled}
          notFoundContent={t(`${I18N}.pdfSelectEmpty`)}
        />
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            color: '#6b7280',
            marginBottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {t(`${I18N}.anchorLabel`)}
          <Tooltip title={t(`${I18N}.anchorHelp`)}>
            <InfoCircleOutlined style={{ fontSize: 12, color: '#0179C1' }} />
          </Tooltip>
        </div>
        <Input
          value={placement.textAnchor}
          onChange={(e) => onChange({ textAnchor: e.target.value })}
          placeholder={t(`${I18N}.anchorPlaceholder`)}
          disabled={disabled}
        />
      </div>
      {/*
       * Match the label-row height above (font-size 11 + 4 px margin ≈ 19 px)
       * so the delete button sits flush with the input — visually centred on
       * the row rather than floating above it.
       */}
      <div style={{ paddingTop: 19 }}>
        <Tooltip title={t(`${I18N}.deletePlacement`)}>
          <Button
            {...ICON_BTN_PROPS}
            danger
            icon={<DeleteOutlined />}
            onClick={onDelete}
            disabled={disabled}
          />
        </Tooltip>
      </div>
    </div>
  );
}

// ---- Draft <-> wire shape conversions --------------------------------------

function draftsFromConfig(config: OneSpanConfig | null | undefined): {
  advisor: AdvisorDraft;
  applicants: ApplicantDraft[];
} {
  if (!config) return { advisor: { placements: [] }, applicants: [] };

  // Split signers by type. There's exactly one advisor by design; everything
  // else is an applicant.
  const advisorRole = config.roles.find((r) => r.type === 'advisor');
  const applicantRoles = config.roles.filter((r) => r.type === 'applicant');

  // For each role, collect placements (pdfEntryId + anchor) by walking the
  // documents tree once and grouping by role slug. This is the inverse of
  // buildWirePayload's "group placements by pdf" step.
  // Per-placement `name`/`binding` are also captured so we can preserve the
  // wire identity across saves (sig_field_1 stays bound to the same logical
  // placement even after rows change).
  const placementsBySlug = new Map<string, PlacementDraft[]>();
  for (const doc of config.documents) {
    for (const f of doc.fields) {
      const list = placementsBySlug.get(f.role) ?? [];
      list.push({
        _uid: newUid(),
        persistedName: f.name,
        persistedBinding: f.binding,
        pdfEntryId: doc.pdfEntryId,
        textAnchor: f.textAnchor,
      });
      placementsBySlug.set(f.role, list);
    }
  }

  const unset = (id: string | undefined) => (!id || id === EMPTY_GUID ? null : id);

  const advisor: AdvisorDraft = {
    placements: advisorRole ? (placementsBySlug.get(advisorRole.slug) ?? []) : [],
  };
  const applicants: ApplicantDraft[] = applicantRoles.map((r) => ({
    _uid: newUid(),
    persistedSlug: r.slug,
    nameFieldId: unset(r.fieldMappings?.nameFieldId),
    lastNameFieldId: unset(r.fieldMappings?.lastNameFieldId),
    emailFieldId: unset(r.fieldMappings?.emailFieldId),
    phoneFieldId: unset(r.fieldMappings?.phoneFieldId),
    // Legacy roles saved before this field existed fall back to the default
    // display name rather than showing a blank input.
    labelEn: r.labelEn ?? DEFAULT_LABEL_EN,
    labelFr: r.labelFr ?? DEFAULT_LABEL_FR,
    conditionCombinator: r.conditionCombinator ?? null,
    conditions: r.conditions ?? [],
    placements: placementsBySlug.get(r.slug) ?? [],
  }));
  return { advisor, applicants };
}

function buildWirePayload(advisor: AdvisorDraft, applicants: ApplicantDraft[]): OneSpanConfig {
  // Stable applicant slug derivation: prefer the persisted slug captured at
  // load time so downstream consumers (OneSpan webhooks, audit logs) don't
  // rebind when the originator deletes/reorders applicants. Fresh applicants
  // pick up the lowest free position-derived slug.
  const usedSlugs = new Set<string>(
    applicants.map((a) => a.persistedSlug).filter((s): s is string => s !== null),
  );
  const nextFreeSlug = () => {
    for (let n = 1; ; n++) {
      const candidate = n === 1 ? 'applicant' : `applicant_${n}`;
      if (!usedSlugs.has(candidate)) {
        usedSlugs.add(candidate);
        return candidate;
      }
    }
  };
  const applicantSlugs = applicants.map((a) => a.persistedSlug ?? nextFreeSlug());

  // Build roles in two passes so applicants take the leading indexes and
  // the advisor lands at the end ONLY when there are advisor placements —
  // no phantom advisor for an unused signer.
  let order = 1;
  const roles: SignatoryRole[] = applicants.map((a, i) => ({
    slug: applicantSlugs[i]!,
    type: 'applicant',
    index: order++,
    fieldMappings: {
      nameFieldId: a.nameFieldId ?? EMPTY_GUID,
      lastNameFieldId: a.lastNameFieldId ?? EMPTY_GUID,
      emailFieldId: a.emailFieldId ?? EMPTY_GUID,
      phoneFieldId: a.phoneFieldId ?? EMPTY_GUID,
    } as SignatoryFieldMappings,
    labelEn: a.labelEn.trim() || DEFAULT_LABEL_EN,
    labelFr: a.labelFr.trim(),
    conditionCombinator: a.conditions.length > 0 ? a.conditionCombinator : null,
    conditions: a.conditions,
  }));
  if (advisor.placements.some((p) => p.pdfEntryId)) {
    roles.push({
      slug: ADVISOR_SLUG,
      type: 'advisor',
      index: order++,
      fieldMappings: null,
      labelEn: null,
      labelFr: null,
    });
  }

  // Group every placement by pdfEntryId. Skip rows missing a PDF (the
  // validator already blocked the save if there were partial rows we
  // couldn't drop). Preserve each placement's stable `name`/`binding` when
  // present; assign the lowest free per-doc number to fresh rows.
  type WireField = { name: string; binding: string; textAnchor: string; role: string };
  const byPdf = new Map<string, { used: Set<number>; fields: (WireField | null)[] }>();

  // First pass: place rows that carry a persisted name/binding at the index
  // their suffix implies (e.g. sig_field_3 → slot 2). Reserves their numbers
  // so subsequent fresh rows fill the gaps without colliding.
  const parseSuffix = (s: string): number => {
    const m = /(\d+)$/.exec(s);
    return m ? parseInt(m[1]!, 10) : NaN;
  };
  type Pending = { slug: string; p: PlacementDraft };
  const pendingByPdf = new Map<string, Pending[]>();

  const enqueue = (slug: string, placements: PlacementDraft[]) => {
    for (const p of placements) {
      if (!p.pdfEntryId) continue;
      const arr = pendingByPdf.get(p.pdfEntryId) ?? [];
      arr.push({ slug, p });
      pendingByPdf.set(p.pdfEntryId, arr);
    }
  };
  applicants.forEach((a, i) => enqueue(applicantSlugs[i]!, a.placements));
  enqueue(ADVISOR_SLUG, advisor.placements);

  for (const [pdfEntryId, pending] of pendingByPdf) {
    const used = new Set<number>();
    const slots: (WireField | null)[] = [];
    // Pass 1 — placements with persisted name keep their slot.
    for (const { slug, p } of pending) {
      if (!p.persistedName) continue;
      const n = parseSuffix(p.persistedName);
      if (!Number.isFinite(n) || n < 1 || used.has(n)) continue;
      used.add(n);
      while (slots.length < n) slots.push(null);
      slots[n - 1] = {
        name: p.persistedName,
        binding: p.persistedBinding ?? `field_${n}`,
        textAnchor: p.textAnchor.trim(),
        role: slug,
      };
    }
    // Pass 2 — fresh placements fill the lowest free numbers.
    let cursor = 1;
    const nextFree = (): number => {
      while (used.has(cursor)) cursor++;
      used.add(cursor);
      return cursor;
    };
    for (const { slug, p } of pending) {
      if (p.persistedName && Number.isFinite(parseSuffix(p.persistedName))) continue;
      const n = nextFree();
      while (slots.length < n) slots.push(null);
      slots[n - 1] = {
        name: `sig_field_${n}`,
        binding: `field_${n}`,
        textAnchor: p.textAnchor.trim(),
        role: slug,
      };
    }
    byPdf.set(pdfEntryId, { used, fields: slots });
  }

  const documents: SignatureDocument[] = Array.from(byPdf.entries()).map(
    ([pdfEntryId, { fields }]) => ({
      pdfEntryId,
      // Filter out unfilled slots (impossible by construction but defensive).
      fields: fields.filter((f): f is WireField => f !== null),
    }),
  );

  return { roles, documents };
}

function validateDraft(
  advisor: AdvisorDraft,
  applicants: ApplicantDraft[],
  pdfChoices: ReadonlyArray<{ id: string }>,
  t: (key: string) => string,
): string | null {
  // Required mappings: name, email, phone must all be picked on every
  // applicant. Last name stays optional (many BMO forms collect full-name
  // in one field). Asterisks in the UI now match this rule.
  for (const a of applicants) {
    if (!a.labelEn.trim()) return t(`${I18N}.validation.signatoryLabelRequired`);
    if (!a.nameFieldId) return t(`${I18N}.validation.signatoryNameRequired`);
    if (!a.emailFieldId) return t(`${I18N}.validation.signatoryEmailRequired`);
    if (!a.phoneFieldId) return t(`${I18N}.validation.signatoryPhoneRequired`);
  }

  const allPlacements: { signer: string; p: PlacementDraft }[] = [
    ...advisor.placements.map((p) => ({ signer: t(`${I18N}.typeAdvisor`), p })),
    ...applicants.flatMap((a, i) =>
      a.placements.map((p) => ({
        signer: `${t(`${I18N}.typeSignatory`)} ${i + 1}`,
        p,
      })),
    ),
  ];
  const knownPdfIds = new Set(pdfChoices.map((c) => c.id));
  for (const { p } of allPlacements) {
    // Blank-row guard: an unfilled row would silently vanish on save.
    // Force the originator to either delete it or fill it.
    if (!p.textAnchor.trim() && !p.pdfEntryId) return t(`${I18N}.validation.emptyRow`);
    if (!p.textAnchor.trim()) return t(`${I18N}.validation.anchorRequired`);
    if (!p.pdfEntryId) return t(`${I18N}.validation.pdfRequired`);
    // Stale-PDF guard: the originator can't save a row pointing at an
    // entry that no longer exists on the PDF Templates step.
    if (!knownPdfIds.has(p.pdfEntryId)) return t(`${I18N}.validation.pdfMissing`);
  }
  // Anchor uniqueness within the same PDF — across ALL signers, because the
  // wire payload groups them together.
  const anchorsByPdf = new Map<string, Set<string>>();
  for (const { p } of allPlacements) {
    if (!p.pdfEntryId || !p.textAnchor.trim()) continue;
    const set = anchorsByPdf.get(p.pdfEntryId) ?? new Set<string>();
    if (set.has(p.textAnchor.trim())) return t(`${I18N}.validation.anchorDuplicate`);
    set.add(p.textAnchor.trim());
    anchorsByPdf.set(p.pdfEntryId, set);
  }
  return null;
}

// ---- Misc helpers ----------------------------------------------------------

function newUid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `uid-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}
